import { Action } from "./actions";
import {
  LoadingEmployeeRateInfo,
  LoadingInitial,
  LoadingPerDiemRateInfo,
  Ready,
  Reloading,
  State,
} from "./states";
import { PerDiemKind } from "~/gql/types";
import { FieldChanges, RateInputs } from "./types";
import {
  handleMatchRateAmount,
  handleMatchUnitAndQuantity,
} from "./handleMatchRateAmount";

function reduceLoadingInitial(state: LoadingInitial, action: Action): State {
  if (action.tag === "form-data-loaded") {
    const rateSheetName = action.rateSheet?.name ?? null;

    return {
      ...state,
      tag: "ready",
      rateSheet: action.rateSheet,
      formFields: { ...state.formFields, rateSheetName },
      outboundFieldMutations: { rateSheetName },
      validationMessages: null,
    };
  }

  if (action.tag === "form-data-failed") {
    return {
      ...state,
      tag: "load-failed",
      error: action.error,
    };
  }

  return state;
}

// the only state that accepts field changes. each one clears the
// "Rate amount updated" message unless it fills the rate amount again.
function reduceReady(state: Ready, action: Action): State {
  if (action.tag === "outbound-fields-written") {
    return {
      ...state,
      outboundFieldMutations: null,
    };
  }

  if (action.tag === "rate-sheet-dependency-changed") {
    const { date, project, useCustomRate } = action.formFields;

    if (useCustomRate || !date || !project) {
      return {
        ...state,
        tag: "ready",
        formFields: {
          ...state.formFields,
          date,
          project,
          useCustomRate,
          rate: null,
          rateSheetName: null,
        },
        rateSheet: null,
        outboundFieldMutations: {
          ...state.outboundFieldMutations,
          rate: null,
          rateSheetName: null,
        },
        validationMessages: null,
      };
    }

    // the rate and rate sheet are kept until the reload shows whether the
    // effective rate sheet changed
    return {
      ...state,
      tag: "reloading",
      formFields: { ...state.formFields, date, project, useCustomRate },
      validationMessages: null,
    };
  }

  if (action.tag === "rate-changed") {
    const rate = action.value;
    const { employee, project } = state.formFields;

    const formFields = { ...state.formFields, rate };

    if (!rate) {
      return {
        ...state,
        formFields,
        validationMessages: null,
      };
    }

    // update unit and quantity fields according to newly selected rate value
    const outboundFieldMutations: FieldChanges = {
      ...state.outboundFieldMutations,
      ...handleMatchUnitAndQuantity({
        rate,
        previousRate: state.formFields.rate,
      }),
    };

    // skip rate amount matching if no project selected
    if (!project) {
      return {
        ...state,
        formFields,
        outboundFieldMutations,
        validationMessages: null,
      };
    }

    // update the rate amount field based on new rate amount, or fetch more rate info
    const result = handleMatchRateAmount({ rate, employee, project });

    switch (result.tag) {
      case "RateAmountMatched":
        return {
          ...state,
          formFields,
          outboundFieldMutations: {
            ...outboundFieldMutations,
            rateAmount: result.matchingRateAmount,
          },
          validationMessages: {
            rateAmount: "Rate amount updated",
          },
        };
      case "RateAmountCleared":
        return {
          ...state,
          formFields,
          outboundFieldMutations: {
            ...outboundFieldMutations,
            rateAmount: null,
          },
          validationMessages: null,
        };
      case "EmployeeRateRequired":
        return {
          ...state,
          tag: "loading-employee-rate-info",
          formFields,
          outboundFieldMutations,
          validationMessages: null,
        };
      case "PerDiemRateRequired":
        return {
          ...state,
          tag: "loading-per-diem-rate-info",
          matchedRatePerDiemKind: result.perDiemKind,
          formFields,
          outboundFieldMutations,
          validationMessages: null,
        };
      case "NoMatchFound":
        return {
          ...state,
          formFields,
          outboundFieldMutations,
          validationMessages: null,
        };
    }
  }

  // a new employee re-resolves the rate amount only: no reload, and no unit
  // or quantity change
  if (action.tag === "employee-changed") {
    const employee = action.value;
    const formFields = { ...state.formFields, employee };
    const { rate, project } = state.formFields;

    if (!rate || !project) {
      return {
        ...state,
        formFields,
        validationMessages: null,
      };
    }

    // update rate amount only if rate is a task rate and uses employee rate
    if (rate.__typename === "TaskRate" && rate.usesEmployeeRate) {
      // reset rate amount field for no employee
      if (!employee) {
        return {
          ...state,
          formFields,
          outboundFieldMutations: {
            ...state.outboundFieldMutations,
            rateAmount: `${0}`,
          },
          validationMessages: {
            rateAmount: "Rate amount updated",
          },
        };
      }
      return {
        ...state,
        tag: "loading-employee-rate-info",
        formFields,
        validationMessages: null,
      };
    }

    // other rates don't depend on the employee, so the rate amount is left alone
    return {
      ...state,
      formFields,
      validationMessages: null,
    };
  }

  return state;
}

function reduceReloading(state: Reloading, action: Action): State {
  if (action.tag === "outbound-fields-written") {
    return {
      ...state,
      outboundFieldMutations: null,
    };
  }

  if (action.tag === "form-data-loaded") {
    const rateSheetName = action.rateSheet?.name ?? null;

    // the selected rate only survives a reload that kept the same
    // effective rate sheet
    if (
      state.formFields.rate &&
      rateSheetName !== state.formFields.rateSheetName
    ) {
      return {
        ...state,
        tag: "ready",
        rateSheet: action.rateSheet,
        formFields: { ...state.formFields, rate: null, rateSheetName },
        outboundFieldMutations: {
          ...state.outboundFieldMutations,
          rate: null,
          rateSheetName,
        },
      };
    }

    return {
      ...state,
      tag: "ready",
      rateSheet: action.rateSheet,
      formFields: { ...state.formFields, rateSheetName },
      outboundFieldMutations: {
        ...state.outboundFieldMutations,
        rateSheetName,
      },
    };
  }

  if (action.tag === "form-data-failed") {
    return {
      ...state,
      tag: "load-failed",
      error: action.error,
    };
  }

  return state;
}

function reduceLoadingPerDiemRateInfo(
  state: LoadingPerDiemRateInfo,
  action: Action
): State {
  if (action.tag === "outbound-fields-written") {
    return {
      ...state,
      outboundFieldMutations: null,
    };
  }

  if (action.tag === "per-diem-rate-loaded") {
    const kind = state.matchedRatePerDiemKind;
    const perDiemRate = action.perDiemRate;

    // no per diem rate on record leaves the rate amount alone
    if (!perDiemRate) {
      return {
        ...state,
        tag: "ready",
      };
    }

    // grab the per diem rate amount based on the kind of rate that's selected
    let rateAmount: number;
    switch (kind) {
      case PerDiemKind.Lodging:
        rateAmount = perDiemRate.lodgingBillable;
        break;
      case PerDiemKind.Meals:
        rateAmount = perDiemRate.mealsBillable;
        break;
      case PerDiemKind.Travel:
      case PerDiemKind.TravelMeals:
        rateAmount = perDiemRate.travelBillable;
    }

    return {
      ...state,
      tag: "ready",
      validationMessages: {
        rateAmount: "Rate amount updated",
      },
      outboundFieldMutations: {
        ...state.outboundFieldMutations,
        rateAmount: `${rateAmount}`,
      },
    };
  }

  if (action.tag === "extended-rate-failed") {
    return {
      ...state,
      tag: "load-extended-failed",
      error: action.error,
    };
  }

  return state;
}

function reduceLoadingEmployeeRateInfo(
  state: LoadingEmployeeRateInfo,
  action: Action
): State {
  if (action.tag === "outbound-fields-written") {
    return {
      ...state,
      outboundFieldMutations: null,
    };
  }

  if (action.tag === "employee-rate-loaded") {
    return {
      ...state,
      tag: "ready",
      validationMessages: {
        rateAmount: "Rate amount updated",
      },
      outboundFieldMutations: {
        ...state.outboundFieldMutations,
        rateAmount: `${action.employeeRate}`,
      },
    };
  }

  if (action.tag === "extended-rate-failed") {
    return {
      ...state,
      tag: "load-extended-failed",
      error: action.error,
    };
  }

  return state;
}

// the machine's starting state for the form's initial rate inputs. there's
// only a rate sheet to load when the date and project are set and the rate
// isn't overridden.
export function initialState(formFields: RateInputs): State {
  const { date, project, useCustomRate } = formFields;

  if (useCustomRate || !date || !project) {
    return {
      tag: "ready",
      formFields: { ...formFields, rateSheetName: null },
      rateSheet: null,
      outboundFieldMutations: { rateSheetName: null },
      validationMessages: null,
    };
  }

  return {
    tag: "loading-initial",
    formFields,
  };
}

export function reducer(state: State, action: Action): State {
  // triggered on initial form load.
  if (state.tag === "loading-initial") {
    return reduceLoadingInitial(state, action);
  }

  if (state.tag === "ready") {
    return reduceReady(state, action);
  }

  if (state.tag === "reloading") {
    return reduceReloading(state, action);
  }

  if (state.tag === "loading-per-diem-rate-info") {
    return reduceLoadingPerDiemRateInfo(state, action);
  }

  if (state.tag === "loading-employee-rate-info") {
    return reduceLoadingEmployeeRateInfo(state, action);
  }

  return state;
}
